// SubscriptionManagement.jsx - Component for managing Pro subscription (cancel/reactivate)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionManagement = ({ user, onSubscriptionUpdate }) => {
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const API_URL = import.meta.env.VITE_SERVER_URL || 'http://localhost:4000';  // Node.js server URL

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPlanName = (planId) => {
    const plans = {
      'pro_monthly': 'Pro Monthly',
      'pro_yearly': 'Pro Yearly',
      'premium_monthly': 'Premium Monthly',
      'premium_yearly': 'Premium Yearly'
    };
    return plans[planId] || 'Pro Plan';
  };

  // Handle subscription cancellation (keeps access until period ends)
  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payment/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'  // Include auth cookies
      });

      const data = await response.json();
      
      if (data.success) {
        // Update local user state to reflect cancellation
        const updatedUser = {
          ...user,
          subscriptionCancelled: true,
          cancelledAt: new Date().toISOString()
        };
        
        onSubscriptionUpdate(updatedUser);  // Update parent component state
        setShowCancelModal(false);
        
        alert('Your subscription has been cancelled successfully. You will retain Pro access until your current billing period ends.');
      } else {
        throw new Error(data.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Cancel subscription error:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payment/reactivate-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success) {
        const updatedUser = {
          ...user,
          subscriptionCancelled: false,
          cancelledAt: null
        };
        
        onSubscriptionUpdate(updatedUser);
        alert('Your subscription has been reactivated successfully!');
      } else {
        throw new Error(data.message || 'Failed to reactivate subscription');
      }
    } catch (error) {
      console.error('Reactivate subscription error:', error);
      alert('Failed to reactivate subscription. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Don't show if user is not pro
  if (!user?.pro && !user?.subscriptionCancelled) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
            <Icon name="CreditCard" size={20} color="var(--color-muted-foreground)" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Subscription
            </h3>
            <p className="text-sm text-muted-foreground">
              Manage your subscription plan
            </p>
          </div>
        </div>

        <div className="text-center py-8">
          <div className="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Crown" size={24} color="var(--color-muted-foreground)" />
          </div>
          <h4 className="text-foreground font-medium mb-2">Free Plan</h4>
          <p className="text-sm text-muted-foreground mb-4">
            You're currently on the free plan with limited features.
          </p>
          <Button 
            onClick={() => navigate('/plans')}
            className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
            iconName="Crown"
            iconPosition="left"
          >
            Upgrade to Pro
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center">
            <Icon name="Crown" size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Pro Subscription
            </h3>
            <p className="text-sm text-muted-foreground">
              Manage your Pro membership
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Current Plan */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Current Plan</span>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-semibold text-foreground">
                  {getPlanName(user?.planId)}
                </span>
                {user?.subscriptionCancelled ? (
                  <span className="px-2 py-1 bg-warning/10 text-warning text-xs rounded-full font-medium">
                    Cancelled
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full font-medium">
                    Active
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <span className="text-xs text-muted-foreground block">Started</span>
                <span className="text-sm text-foreground font-medium">
                  {formatDate(user?.subscriptionStartDate)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">
                  {user?.subscriptionCancelled ? 'Expires' : 'Next Billing'}
                </span>
                <span className="text-sm text-foreground font-medium">
                  {formatDate(user?.subscriptionEndDate)}
                </span>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">Active Benefits</h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Icon name="Check" size={16} color="var(--color-success)" />
                <span className="text-sm text-muted-foreground">Unlimited news generation</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Check" size={16} color="var(--color-success)" />
                <span className="text-sm text-muted-foreground">Unlimited fact checking</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Check" size={16} color="var(--color-success)" />
                <span className="text-sm text-muted-foreground">Unlimited bias detection</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Check" size={16} color="var(--color-success)" />
                <span className="text-sm text-muted-foreground">Priority support</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 pt-4 border-t border-border">
            {user?.subscriptionCancelled ? (
              <Button
                onClick={handleReactivateSubscription}
                loading={loading}
                className="flex-1 bg-success hover:bg-success/90"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Reactivate Subscription
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate('/plans')}
                  className="flex-1"
                  iconName="ArrowUpCircle"
                  iconPosition="left"
                >
                  Change Plan
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(true)}
                  className="flex-1 border-warning/20 text-warning hover:bg-warning/5"
                  iconName="X"
                  iconPosition="left"
                >
                  Cancel Subscription
                </Button>
              </>
            )}
          </div>

          {user?.subscriptionCancelled && (
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">
                    Subscription Cancelled
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Your Pro access will continue until {formatDate(user?.subscriptionEndDate)}. 
                    After that, you'll be moved to the free plan with limited features.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-elevated border border-border max-w-md w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground">
                    Cancel Subscription
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to cancel?
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCancelModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-foreground mb-2">What happens when you cancel:</h3>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <Icon name="Clock" size={16} color="var(--color-muted-foreground)" className="mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      You'll keep Pro access until {formatDate(user?.subscriptionEndDate)}
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="ArrowDown" size={16} color="var(--color-muted-foreground)" className="mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      After that, you'll be moved to the free plan with limited features
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="RefreshCw" size={16} color="var(--color-muted-foreground)" className="mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      You can reactivate anytime before your subscription expires
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-error/5 border border-error/20 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">No refunds:</strong> You won't receive a refund for the current billing period.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center space-x-3 p-6 border-t border-border">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCancelModal(false)}
                disabled={loading}
              >
                Keep Subscription
              </Button>
              <Button
                className="flex-1 bg-error hover:bg-error/90"
                onClick={handleCancelSubscription}
                loading={loading}
                iconName="X"
                iconPosition="left"
              >
                Cancel Subscription
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionManagement;